/**
 * Prop Studio — placing a library model in a prop's frame.
 *
 * The one placement contract the page and the Blender renderer both implement
 * (scripts/props/README.md, "Placement"); render_room.py holds the other half.
 * If you change one, change the other. Pure arithmetic, no three.js, so it
 * runs under jest and the viewer and the readouts cannot disagree.
 *
 * Frames. A FLOOR prop stands on the floor centred on the origin facing +z:
 * x along the wall it stands against, y up, z out into the room — FEET. A
 * library model's bounds are recorded in ITS OWN file: metres, Blender axes
 * (Z up, −Y the side it was built to face). Its .glb loads Y-up with that
 * front on +z, so Blender (x, y, z) is glTF (x, z, −y).
 */

/** Metres in a foot. */
export const FT = 0.3048;

export type Vec3 = [number, number, number];

/** An axis-aligned box. */
export interface Bounds { min: Vec3; max: Vec3 }

/** Which way the copies of a model run: stacked, along the wall, or out into the room. */
export type CopyAxis = 'up' | 'along' | 'into';

/**
 * The model repeated: `count` of it, each the one before moved along `axis`
 * by the placed model's extent that way plus `gap` feet — two crates make a
 * stack, three bins a row. `count: 1` is the model once.
 */
export interface PropCopies { count: number; axis: CopyAxis; gap: number }

/**
 * How a model sits in its prop's frame: uniform `scale` (the model stays in
 * metres), `yaw` degrees about the up axis (counter-clockwise seen from
 * above), `offset` in FEET along the wall / up / into the room, `mirror`
 * (the model's x negated — a bike facing the other way), and `copies`.
 */
/**
 * Which point of the (mirrored, stood) model goes to the prop's origin:
 * its floor-centre — a floor prop stands on the floor; something that sits
 * on a shelf or in a basket sits at the hold point — or its top-centre, for
 * something that hangs from a hook. Absent means floor.
 */
export type PropAnchor = 'floor' | 'top';

/**
 * The box the DESIGNER stages and draws this model at, in feet (w along the
 * wall, d into the room, h up), when its placed bounds are not the right
 * thing to stage round — a vanity whose model carries a mirror that towers
 * over the cabinet, say. Absent: the placed bounds (copies and parts in). The
 * renderer never reads it; it is Prop Studio's word on the stand-in's size.
 */
export interface StageBox { w: number; d: number; h: number }

export interface PropTransform {
  scale: number;
  yaw: number;
  anchor?: PropAnchor;
  /**
   * The hang point: the spot ON THE MODEL that goes to the prop's origin (a
   * hung prop's hook, the gold ball) — the grip of a shovel, the hole in a
   * pump's handle — picked on the model in Prop Studio. In the model's own
   * frame, as its bounds are (Blender axes, metres, before anything is done
   * to it). Absent: the anchor's point of the box (its top- or
   * floor-centre). README "Held props: the hang point".
   */
  hang?: Vec3;
  /**
   * Standing the model up, before anything else (README step 0): `tilt`
   * degrees about the model's own left-right axis (Blender x), then `roll`
   * about its front-back axis (Blender y). A model built lying on its back
   * takes a tilt of 90. Both default to 0.
   */
  tilt: number;
  roll: number;
  offset: Vec3;
  mirror: boolean;
  copies: PropCopies;
  /** The designer's staging box, when it is not the placed bounds (StageBox). */
  stage?: StageBox;
  /**
   * At most this many of THIS model on one wall (a hung or wall prop) or in one
   * room (a floor or ceiling prop), 1 … 12: two identical boots side by side
   * are not wanted; a prop's other models may still stand beside it. Absent:
   * no limit. Staging picks among the models still under their limit, and
   * leaves a copy out when none is.
   */
  perWall?: number;
  /**
   * The spaces THIS model is staged in (space type ids), among the prop's own:
   * a shelving unit's steel model for the garage and shed, its oak one for the
   * basement. Absent: every space the prop is in. Empty: nowhere. Staging
   * picks a copy's model among those for the room's space (README step 8).
   */
  spaces?: string[];
}

export const MAX_PER_WALL = 12;

export const DEFAULT_COPIES: PropCopies = { count: 1, axis: 'up', gap: 0 };
export const MAX_COPIES = 12;
export const DEFAULT_TRANSFORM: PropTransform = { scale: 1, yaw: 0, tilt: 0, roll: 0, offset: [0, 0, 0], mirror: false, copies: DEFAULT_COPIES };

/** The prop-frame axis each copy direction runs along: x along the wall, y up, z into the room. */
export const COPY_AXIS_INDEX: Record<CopyAxis, 0 | 1 | 2> = { along: 0, up: 1, into: 2 };

/** A 3 × 3 rotation, row-major, glTF axes. */
export type Mat3 = [number, number, number, number, number, number, number, number, number];
/** A unit quaternion [x, y, z, w], as three.js keeps one. */
export type Quat = [number, number, number, number];

/**
 * What the viewer sets on the loaded glTF scene: feet, three.js axes. The
 * rotation is the whole of it — stand (tilt, roll) and yaw — as a matrix,
 * and as the quaternion three's `object.quaternion` takes.
 */
export interface ThreePlacement { position: Vec3; rotation: Mat3; quaternion: Quat; scale: Vec3 }

export const degToRad = (deg: number): number => (deg * Math.PI) / 180;

const EPS = 1e-6;

/** Blender (x, y, z) → glTF (x, z, −y): the axis swap the glTF importer carries. */
export const blenderToGltf = (v: Vec3): Vec3 => [v[0], v[2], -v[1]];
/** glTF (three.js, Y up) → Blender (Z up): the inverse of blenderToGltf. */
export const gltfToBlender = (v: Vec3): Vec3 => [v[0], -v[2], v[1]];

export const rotX = (rad: number): Mat3 => { const c = Math.cos(rad), s = Math.sin(rad); return [1, 0, 0, 0, c, -s, 0, s, c]; };
export const rotY = (rad: number): Mat3 => { const c = Math.cos(rad), s = Math.sin(rad); return [c, 0, s, 0, 1, 0, -s, 0, c]; };
export const rotZ = (rad: number): Mat3 => { const c = Math.cos(rad), s = Math.sin(rad); return [c, -s, 0, s, c, 0, 0, 0, 1]; };
export const mat3Multiply = (a: Mat3, b: Mat3): Mat3 => {
  const out = new Array(9).fill(0) as Mat3;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
  return out;
};
export const applyMat3 = (m: Mat3, v: Vec3): Vec3 => [
  m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
  m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
  m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
];

/** Rotation about the up axis, three's `rotation.y` sense: +θ is counter-clockwise seen from above. */
export function rotateY(v: Vec3, rad: number): Vec3 {
  return applyMat3(rotY(rad), v);
}

/**
 * The unit quaternion of a rotation matrix (the standard conversion, w ≥ 0 —
 * what three's `setFromRotationMatrix` makes, so the viewer's object and
 * this agree to the sign).
 */
export function quaternionOf(m: Mat3): Quat {
  const [m11, m12, m13, m21, m22, m23, m31, m32, m33] = m;
  const trace = m11 + m22 + m33;
  let q: Quat;
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1);
    q = [(m32 - m23) * s, (m13 - m31) * s, (m21 - m12) * s, 0.25 / s];
  } else if (m11 > m22 && m11 > m33) {
    const s = 2 * Math.sqrt(1 + m11 - m22 - m33);
    q = [0.25 * s, (m12 + m21) / s, (m13 + m31) / s, (m32 - m23) / s];
  } else if (m22 > m33) {
    const s = 2 * Math.sqrt(1 + m22 - m11 - m33);
    q = [(m12 + m21) / s, 0.25 * s, (m23 + m32) / s, (m13 - m31) / s];
  } else {
    const s = 2 * Math.sqrt(1 + m33 - m11 - m22);
    q = [(m13 + m31) / s, (m23 + m32) / s, 0.25 * s, (m21 - m12) / s];
  }
  return q[3] < 0 ? [-q[0], -q[1], -q[2], -q[3]] : q;
}

/** The model's size in metres, Blender axes: [along the wall, into the room, up]. */
export function modelSizeMetres(b: Bounds): Vec3 {
  return [b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]];
}

/**
 * README step 0, standing the model up, as a rotation in glTF axes: `tilt`
 * about the model's Blender x (glTF x: the same angle) then `roll` about its
 * Blender y (glTF −z: the angle's sign flips).
 */
export function standRotation(t: Pick<PropTransform, 'tilt' | 'roll'>): Mat3 {
  return mat3Multiply(rotZ(-degToRad(t.roll ?? 0)), rotX(degToRad(t.tilt ?? 0)));
}

/** The whole rotation of a placement: the yaw on top of the stand. */
export function fullRotation(t: Pick<PropTransform, 'yaw' | 'tilt' | 'roll'>): Mat3 {
  return mat3Multiply(rotY(degToRad(t.yaw)), standRotation(t));
}

/** The eight corners of a box (Blender axes) in glTF axes. */
function cornersGltf(b: Bounds): Vec3[] {
  const out: Vec3[] = [];
  for (const x of [b.min[0], b.max[0]]) for (const y of [b.min[1], b.max[1]]) for (const z of [b.min[2], b.max[2]]) out.push(blenderToGltf([x, y, z]));
  return out;
}

/** The axis-aligned box round some points. */
function boxOf(points: Vec3[]): Bounds {
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const q of points) for (let i = 0; i < 3; i++) { if (q[i] < min[i]) min[i] = q[i]; if (q[i] > max[i]) max[i] = q[i]; }
  return { min, max };
}

/**
 * The model's floor-centre in glTF axes (metres) once it is mirrored and
 * stood up (steps 0 and 2, at scale 1): the middle of the stood box in x and
 * z, the bottom of it in y. Subtracting it stands the model on the floor
 * centred on the origin (README step 1).
 */
export function floorCentreGltf(b: Bounds, t: Pick<PropTransform, 'tilt' | 'roll' | 'mirror'> = { tilt: 0, roll: 0, mirror: false }): Vec3 {
  return anchorPointGltf(b, { ...t, anchor: 'floor' });
}

/**
 * The point of the mirrored, stood model (glTF axes, metres) that goes to
 * the origin: the hang point picked on the model, when there is one, else
 * the box's floor-centre or top-centre.
 */
export function anchorPointGltf(b: Bounds, t: Pick<PropTransform, 'tilt' | 'roll' | 'mirror' | 'anchor' | 'hang'>): Vec3 {
  const stand = standRotation(t);
  const mx = t.mirror ? -1 : 1;
  if (t.hang) {
    const p = blenderToGltf(t.hang);
    return applyMat3(stand, [p[0] * mx, p[1], p[2]]);
  }
  const stood = boxOf(cornersGltf(b).map((p) => applyMat3(stand, [p[0] * mx, p[1], p[2]])));
  const y = t.anchor === 'top' ? stood.max[1] : stood.min[1];
  return [(stood.min[0] + stood.max[0]) / 2, y, (stood.min[2] + stood.max[2]) / 2];
}

/**
 * README steps 0–5 as one three.js transform for a GLTF-loaded (Y-up) model.
 *
 * three composes an object's matrix as position · rotation · scale, so the
 * recentre — a move of the stood model BEFORE it is scaled and turned —
 * folds into the position: `position = offset − Ry(yaw)·k·c`, where c is the
 * floor-centre of the mirrored, stood model, k the metres→feet scale; the
 * rotation is yaw · stand, with the mirror in the scale's x.
 */
export function placeInThree({ modelBounds, transform }: { modelBounds: Bounds; transform: PropTransform }): ThreePlacement {
  const c = anchorPointGltf(modelBounds, transform);
  const k = transform.scale / FT;
  const sx = transform.mirror ? -k : k;
  const turned = rotateY([c[0] * k, c[1] * k, c[2] * k], degToRad(transform.yaw));
  const rotation = fullRotation(transform);
  return {
    position: [transform.offset[0] - turned[0], transform.offset[1] - turned[1], transform.offset[2] - turned[2]],
    rotation,
    quaternion: quaternionOf(rotation),
    scale: [sx, k, k],
  };
}

/** A point of the model (glTF axes, metres) → where it lands in the prop's frame (feet). */
export function placePoint(p: Vec3, placement: ThreePlacement): Vec3 {
  const scaled: Vec3 = [p[0] * placement.scale[0], p[1] * placement.scale[1], p[2] * placement.scale[2]];
  const turned = applyMat3(placement.rotation, scaled);
  return [turned[0] + placement.position[0], turned[1] + placement.position[1], turned[2] + placement.position[2]];
}

/**
 * ONE placed model's axis-aligned bounds in the prop's frame (feet): the eight
 * corners of its box, each put through the placement, then the extremes. The
 * same arithmetic the viewer draws with, so the readout and the picture agree.
 */
export function singlePlacedBoundsFeet(modelBounds: Bounds, transform: PropTransform): Bounds {
  const placement = placeInThree({ modelBounds, transform });
  return boxOf(cornersGltf(modelBounds).map((p) => placePoint(p, placement)));
}

/**
 * README step 6: where each copy stands, as a move in the prop's frame (feet)
 * from where the placement put the first. Copy k is k × (the placed model's
 * extent along the copies' axis + the gap) that way; the first is at zero.
 * The extent is the single placed box's — as scaled and turned — so a bin
 * turned 90° stacks by its turned height and runs along the wall by its
 * turned length.
 */
export function copyOffsetsFeet(modelBounds: Bounds, transform: PropTransform): Vec3[] {
  const copies = transform.copies ?? DEFAULT_COPIES;
  const count = Math.max(1, Math.min(MAX_COPIES, Math.round(copies.count)));
  const axis = COPY_AXIS_INDEX[copies.axis] ?? 1;
  const single = singlePlacedBoundsFeet(modelBounds, transform);
  const step = single.max[axis] - single.min[axis] + Math.max(0, copies.gap);
  const out: Vec3[] = [];
  for (let k = 0; k < count; k++) {
    const move: Vec3 = [0, 0, 0];
    move[axis] = k * step;
    out.push(move);
  }
  return out;
}

/** The box round ALL the copies, in the prop's frame (feet): what the readouts and the wall rule look at. */
export function placedBoundsFeet(modelBounds: Bounds, transform: PropTransform): Bounds {
  const single = singlePlacedBoundsFeet(modelBounds, transform);
  const min: Vec3 = [...single.min] as Vec3;
  const max: Vec3 = [...single.max] as Vec3;
  for (const move of copyOffsetsFeet(modelBounds, transform)) {
    for (let i = 0; i < 3; i++) {
      min[i] = Math.min(min[i], single.min[i] + move[i]);
      max[i] = Math.max(max[i], single.max[i] + move[i]);
    }
  }
  return { min, max };
}

/** The kit's box in the prop's frame (feet): centred on the origin, standing on the floor. */
export function kitBoxBounds(size: { w: number; d: number; h: number }): Bounds {
  return { min: [-size.w / 2, 0, -size.d / 2], max: [size.w / 2, size.h, size.d / 2] };
}

/**
 * Starting values for `scale` at the model's current `yaw` (degrees, default
 * 0): the scale at which the model's size in metres equals the kit box's in
 * feet × 0.3048 for the chosen measure.
 *   footprint — fills the floor plan: the larger of the along-the-wall and
 *               into-the-room ratios (it may stand proud in the other);
 *   height    — as tall as the box;
 *   box       — never bigger than the box in any direction (the smallest ratio).
 * The footprint is taken AS TURNED: the model's box put through the yaw and
 * boxed again, axis-aligned, the way placedBoundsFeet sees it. A bicycle
 * built along Blender y and turned 90° is long along the wall, not into the
 * room; compared at yaw 0 the fits would triple it.
 * The same three fits the v1 renderer chose from; here they are a suggestion,
 * and the numbers the user settles on are what gets saved.
 */
export function fitSuggestions(modelBounds: Bounds, kitSize: { w: number; d: number; h: number }, turned: number | Pick<PropTransform, 'yaw' | 'tilt' | 'roll'> = 0): { footprint: number; height: number; box: number } {
  const t = typeof turned === 'number' ? { yaw: turned, tilt: 0, roll: 0 } : turned;
  // The model's box as stood and turned (scale 1, metres, glTF axes: x along, y up, z deep).
  const box = boxOf(cornersGltf(modelBounds).map((p) => applyMat3(fullRotation(t), p)));
  const alongTurned = box.max[0] - box.min[0];
  const tall = box.max[1] - box.min[1];
  const deepTurned = box.max[2] - box.min[2];
  const rAlong = (kitSize.w * FT) / Math.max(EPS, alongTurned);
  const rDeep = (kitSize.d * FT) / Math.max(EPS, deepTurned);
  const rTall = (kitSize.h * FT) / Math.max(EPS, tall);
  return { footprint: Math.max(rAlong, rDeep), height: rTall, box: Math.min(rAlong, rDeep, rTall) };
}

/**
 * How far the placed model's nearest point is from the wall (feet): positive
 * is clear of the wall, negative is BEHIND it — inside the Wall&CeilingBoard,
 * where the render would show it cut off. The one placement fault the
 * readouts' sizes cannot show, so it gets a line of its own. The wall is at
 * z = −d/2 of a floor prop's kit box, or wherever the frame puts it (a number).
 */
export function wallClearanceFeet(placed: Bounds, wall: { d: number } | number): number {
  const wallZ = typeof wall === 'number' ? wall : -wall.d / 2;
  return placed.min[2] - wallZ;
}

/**
 * Two transforms the same, to the precision the inputs show. The anchor
 * counts: it is the prop's kind's rather than the user's, but a held prop
 * saved without it (by a helper from before anchors) renders from the wrong
 * point, so the page must show it as unsaved until it is written.
 */
export function sameTransform(a: PropTransform, b: PropTransform): boolean {
  const close = (x: number, y: number) => Math.abs(x - y) < 1e-9;
  const ca = a.copies ?? DEFAULT_COPIES, cb = b.copies ?? DEFAULT_COPIES;
  const sameHang = !a.hang || !b.hang ? !a.hang === !b.hang : a.hang.every((v, i) => close(v, (b.hang as Vec3)[i]));
  const sameStage = !a.stage || !b.stage ? !a.stage === !b.stage : close(a.stage.w, b.stage.w) && close(a.stage.d, b.stage.d) && close(a.stage.h, b.stage.h);
  const sameSpaces = !a.spaces || !b.spaces ? !a.spaces === !b.spaces : a.spaces.length === b.spaces.length && a.spaces.every((s) => (b.spaces as string[]).includes(s));
  return close(a.scale, b.scale) && close(a.yaw, b.yaw) && close(a.tilt ?? 0, b.tilt ?? 0) && close(a.roll ?? 0, b.roll ?? 0) && a.mirror === b.mirror
    && (a.anchor ?? 'floor') === (b.anchor ?? 'floor') && sameHang && sameStage && sameSpaces && (a.perWall ?? 0) === (b.perWall ?? 0)
    && close(a.offset[0], b.offset[0]) && close(a.offset[1], b.offset[1]) && close(a.offset[2], b.offset[2])
    && ca.count === cb.count && ca.axis === cb.axis && close(ca.gap, cb.gap);
}

/** A stage box from a manifest entry: three positive finite numbers, else none. */
export function normalizeStageBox(raw: unknown): StageBox | undefined {
  const s = (raw && typeof raw === 'object' ? raw : null) as Partial<Record<keyof StageBox, unknown>> | null;
  if (!s) return undefined;
  const ok = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
  return ok(s.w) && ok(s.d) && ok(s.h) ? { w: s.w, d: s.d, h: s.h } : undefined;
}

/**
 * The size the designer stages and draws a placed model at (feet: w along
 * the wall, d into the room, h up): the transform's stage box when one is
 * set, else the box round the placed model — `placed` is that box in the
 * prop's frame (placedBoundsFeet, with any parts folded in). A stand-in
 * stands on the floor, so a model held off it (a wall-hung vanity, offset up
 * 2 ft) is staged as tall as its TOP, not its own height: what hangs above
 * must clear the model, not the gap under it. Below the floor (a held prop's
 * frame, origin at the hold point) the extent is what there is.
 */
export function stagedSizeFeet(placed: Bounds, transform: Pick<PropTransform, 'stage'>): StageBox {
  if (transform.stage) return { ...transform.stage };
  const h = placed.min[1] >= -EPS ? placed.max[1] : placed.max[1] - placed.min[1];
  return { w: placed.max[0] - placed.min[0], h, d: placed.max[2] - placed.min[2] };
}

/** Copies from a manifest entry — whatever is missing or malformed takes the default (the model once). */
export function normalizeCopies(raw: unknown): PropCopies {
  const c = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof PropCopies, unknown>>;
  const count = typeof c.count === 'number' && Number.isFinite(c.count) ? Math.max(1, Math.min(MAX_COPIES, Math.round(c.count))) : 1;
  const axis: CopyAxis = c.axis === 'along' || c.axis === 'into' || c.axis === 'up' ? c.axis : 'up';
  const gap = typeof c.gap === 'number' && Number.isFinite(c.gap) ? Math.max(0, c.gap) : 0;
  return { count, axis, gap };
}

/** A transform from a manifest entry — whatever is missing or malformed takes the default. */
export function normalizeTransform(raw: unknown): PropTransform {
  const t = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof PropTransform, unknown>>;
  const num = (v: unknown, dflt: number) => (typeof v === 'number' && Number.isFinite(v) ? v : dflt);
  const off = Array.isArray(t.offset) ? t.offset : [];
  const hang = Array.isArray(t.hang) && t.hang.length === 3 && t.hang.every((v) => typeof v === 'number' && Number.isFinite(v))
    ? [t.hang[0], t.hang[1], t.hang[2]] as Vec3
    : undefined;
  const stage = normalizeStageBox(t.stage);
  // The per-wall limit and the spaces this model is staged in: kept as saved (a list of space ids, each once).
  const perWall = typeof t.perWall === 'number' && Number.isInteger(t.perWall) && t.perWall >= 1 && t.perWall <= MAX_PER_WALL ? t.perWall : undefined;
  const spaces = Array.isArray(t.spaces) ? [...new Set(t.spaces.filter((s): s is string => typeof s === 'string' && /^[a-z0-9-]{1,40}$/.test(s)))] : undefined;
  return {
    scale: Math.max(EPS, num(t.scale, 1)),
    yaw: num(t.yaw, 0),
    tilt: num(t.tilt, 0),
    roll: num(t.roll, 0),
    anchor: t.anchor === 'top' ? 'top' : 'floor',
    ...(hang ? { hang } : {}),
    offset: [num(off[0], 0), num(off[1], 0), num(off[2], 0)],
    mirror: t.mirror === true,
    copies: normalizeCopies(t.copies),
    ...(stage ? { stage } : {}),
    ...(perWall !== undefined ? { perWall } : {}),
    ...(spaces ? { spaces } : {}),
  };
}

/** Bounds from a manifest entry, or null when they are not a usable box. */
export function normalizeBounds(raw: unknown): Bounds | null {
  const b = (raw && typeof raw === 'object' ? raw : null) as { min?: unknown; max?: unknown } | null;
  const vec = (v: unknown): Vec3 | null => (Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number' && Number.isFinite(n)) ? [v[0], v[1], v[2]] : null);
  const min = b && vec(b.min);
  const max = b && vec(b.max);
  if (!min || !max) return null;
  return { min, max };
}

/** Where the hang point is before one is set: the box's top-centre (a held prop hung from its top) or bottom-centre. */
export function defaultHang(b: Bounds, anchor: 'top' | 'floor'): Vec3 {
  return [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, anchor === 'top' ? b.max[2] : b.min[2]];
}

/** A hang point as fractions of the model's box (its own frame), and back. */
export const hangToFraction = (b: Bounds, hang: Vec3): Vec3 =>
  hang.map((v, i) => (b.max[i] - b.min[i] > 1e-9 ? (v - b.min[i]) / (b.max[i] - b.min[i]) : 0.5)) as Vec3;
export const fractionToHang = (b: Bounds, f: Vec3): Vec3 =>
  f.map((v, i) => Math.round((b.min[i] + v * (b.max[i] - b.min[i])) * 100000) / 100000) as Vec3;
